import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLink, X } from 'lucide-react';
import { hives, feed, recipes as recipesApi, shared } from '../utils/api';
import { useFeedContext } from '../contexts/FeedContext';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import BuzzCard from '../components/BuzzCard';

// Icons
const BackIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const HeartIcon = ({ filled, size = 18 }) => (
  <svg className={`${filled ? 'fill-red-500 text-red-500' : ''}`} width={size} height={size} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const CommentIcon = ({ size = 18 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const BookmarkIcon = ({ filled, size = 18 }) => (
  <svg width={size} height={size} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const HiveDetailPage = () => {
  const { hiveId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get caching functions from context
  const { fetchHiveDetail, getCachedHiveDetail, updateHiveDetailPosts, invalidateHiveCache } = useFeedContext();
  
  // Initialize state from cache if available
  const cachedDetail = getCachedHiveDetail(hiveId);
  const [hive, setHive] = useState(cachedDetail?.hive || null);
  const [posts, setPosts] = useState(cachedDetail?.posts || []);
  const [members, setMembers] = useState(cachedDetail?.members || []);
  const [loading, setLoading] = useState(!cachedDetail); // Only loading if no cache
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userRecipeIds, setUserRecipeIds] = useState(new Set());
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [showMembersModal, setShowMembersModal] = useState(false);

  // Get back navigation path from state or default to /feed
  const backPath = location.state?.from || '/feed';
  const backTab = location.state?.tab || 'feed';
  const backScrollY = location.state?.scrollY;

  // Fetch hive details and posts (with caching)
  const loadHive = useCallback(async (forceRefresh = false) => {
    try {
      // Only show loading if no cached data
      if (!cachedDetail) {
        setLoading(true);
      }
      
      // Fetch with caching
      const detail = await fetchHiveDetail(hiveId, forceRefresh);
      
      if (detail) {
        setHive(detail.hive);
        setPosts(detail.posts);
        setMembers(detail.members);
      }
      
      // Mark hive as read
      await hives.markRead(hiveId);
      
    } catch (error) {
      console.error('Error loading hive:', error);
      toast.error('Failed to load hive');
      navigate('/feed');
    } finally {
      setLoading(false);
    }
  }, [hiveId, navigate, fetchHiveDetail, cachedDetail]);

  // Load user's recipe IDs to check if they already have recipes
  // Also create a map from source_recipe_id to the user's recipe_id
  const [userRecipeMap, setUserRecipeMap] = useState(new Map()); // source_recipe_id -> recipe_id
  
  const loadUserRecipes = useCallback(async () => {
    try {
      // Use lightweight endpoint for ID mapping only
      const res = await recipesApi.getIdMapping();
      const recipes = res.data || [];
      const allRecipeIds = new Set(recipes.map(r => r.recipe_id));
      setUserRecipeIds(allRecipeIds);
      
      // Create map from source_recipe_id AND recipe_id to user's recipe_id
      const recipeMap = new Map();
      recipes.forEach(r => {
        // Map user's own recipe_id to itself (for viewing own buzzed recipes)
        recipeMap.set(r.recipe_id, r.recipe_id);
        // Map source_recipe_id to user's recipe_id (for saved copies)
        if (r.source_recipe_id) {
          recipeMap.set(r.source_recipe_id, r.recipe_id);
        }
      });
      setUserRecipeMap(recipeMap);
      
      // Get current user ID from cache or auth
      const userCache = localStorage.getItem('userCache');
      if (userCache) {
        try {
          const user = JSON.parse(userCache);
          setCurrentUserId(user.user_id);
        } catch (e) {
          console.error('Error parsing user cache:', e);
        }
      } else {
        try {
          const authRes = await fetch(`${window.location.origin}/api/auth/me`, {
            credentials: 'include'
          });
          if (authRes.ok) {
            const userData = await authRes.json();
            setCurrentUserId(userData.user_id);
          }
        } catch (authError) {
          console.error('Error getting current user:', authError);
        }
      }
    } catch (error) {
      console.error('Error loading user recipes:', error);
    }
  }, []);

  // Initialize likedPosts from posts data when currentUserId is available
  useEffect(() => {
    if (currentUserId && posts.length > 0) {
      const userLikedPosts = new Set();
      posts.forEach(post => {
        if (post.likes?.includes(currentUserId)) {
          userLikedPosts.add(post.buzz_id);
        }
      });
      if (userLikedPosts.size > 0) {
        setLikedPosts(userLikedPosts);
      }
    }
  }, [currentUserId, posts]);

  useEffect(() => {
    loadHive();
    loadUserRecipes();
  }, [loadHive, loadUserRecipes]);

  const handleBack = () => {
    navigate(backPath, { state: { tab: backTab, scrollY: backScrollY } });
  };

  const handleSaveRecipe = async (post) => {
    if (!post.source_recipe_id) {
      toast.error('This recipe cannot be saved');
      return;
    }

    try {
      // Check if already in user's box
      if (userRecipeIds.has(post.source_recipe_id)) {
        toast.info('Recipe already in your box', {
          action: {
            label: 'View Recipe',
            onClick: () => navigate(`/recipe/${post.recipe_id}`)
          }
        });
        return;
      }

      const res = await shared.addToBox(post.source_recipe_id);
      if (res.data.recipe_id) {
        setUserRecipeIds(prev => new Set([...prev, post.source_recipe_id]));
        toast.success('Recipe saved to your box!', {
          action: {
            label: 'View Recipe',
            onClick: () => navigate(`/recipe/${res.data.recipe_id}`)
          }
        });
      }
    } catch (error) {
      console.error('Error saving recipe:', error);
      toast.error('Failed to save recipe');
    }
  };

  // Handle like/unlike post
  const handleLike = async (post) => {
    const buzzId = post.buzz_id;
    const isLiked = likedPosts.has(buzzId) || post.likes?.includes(currentUserId);
    
    try {
      if (isLiked) {
        const response = await feed.unlikePost(buzzId);
        setLikedPosts(prev => {
          const next = new Set(prev);
          next.delete(buzzId);
          return next;
        });
        // Update post likes with response data
        setPosts(prev => prev.map(p => {
          if (p.buzz_id === buzzId) {
            return {
              ...p,
              likes: (p.likes || []).filter(id => id !== currentUserId),
              like_details: (p.like_details || []).filter(u => u.user_id !== currentUserId),
              like_count: response.data?.like_count ?? Math.max(0, (p.like_count || 0) - 1)
            };
          }
          return p;
        }));
      } else {
        const response = await feed.likePost(buzzId);
        setLikedPosts(prev => new Set([...prev, buzzId]));
        // Update post likes with response data
        setPosts(prev => prev.map(p => {
          if (p.buzz_id === buzzId) {
            const newLikeDetail = response.data?.user || { user_id: currentUserId, name: 'You' };
            return {
              ...p,
              likes: [...(p.likes || []), currentUserId],
              like_details: [...(p.like_details || []), newLikeDetail],
              like_count: response.data?.like_count ?? (p.like_count || 0) + 1
            };
          }
          return p;
        }));
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to update like');
    }
  };

  // Handle opening comment modal
  const handleComment = (post) => {
    setSelectedPost(post);
    setShowCommentModal(true);
    setNewComment('');
  };

  // Handle submitting a comment
  const handleSubmitComment = async () => {
    if (!newComment.trim() || !selectedPost) return;
    
    try {
      await feed.addComment(selectedPost.buzz_id, newComment.trim());
      // Update post comments count
      setPosts(prev => prev.map(p => {
        if (p.buzz_id === selectedPost.buzz_id) {
          return {
            ...p,
            comment_count: (p.comment_count || 0) + 1
          };
        }
        return p;
      }));
      setShowCommentModal(false);
      setNewComment('');
      toast.success('Comment added!');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString();
  };

  // Share hive - use invite link for private hives, direct link for public
  const handleShareHive = async () => {
    let shareUrl;
    let shareTitle;
    let shareText;

    if (hive.hive_type === 'private') {
      // For private hives, use the invite code
      const inviteCode = hive.invite_code;
      if (!inviteCode) {
        toast.error('No invite code available');
        return;
      }
      shareUrl = `${window.location.origin}/join-hive?code=${inviteCode}`;
      shareTitle = `Join ${hive.name} on Honey`;
      shareText = `You're invited to join "${hive.name}" - a private recipe share group on Honey! 🍯`;
    } else {
      // For public hives, use OG endpoint for better link previews
      shareUrl = `${window.location.origin}/api/og/hive/${hive.hive_id}`;
      shareTitle = `${hive.name} on Honey`;
      shareText = `Check out "${hive.name}" - a recipe share group on Honey! 🍯`;
    }

    // Try native share first (works great on iOS/Android)
    if (navigator.share) {
      try {
        // Include title and text for better share preview
        await navigator.share({ 
          title: shareTitle,
          text: shareText,
          url: shareUrl 
        });
        return;
      } catch (err) {
        console.log('Share error:', err.name, err.message);
        // User cancelled share - that's fine, just return
        if (err.name === 'AbortError') return;
        // NotAllowedError means not triggered by user gesture - fall through
        // Other errors - fall through to clipboard
      }
    }

    // Desktop fallback - try clipboard (include descriptive text for private hives)
    try {
      const fullShareContent = hive.hive_type === 'private' 
        ? `${shareUrl}\n${shareText}`
        : shareUrl;
      await navigator.clipboard.writeText(fullShareContent);
      toast.success(hive.hive_type === 'private' ? 'Invite link copied!' : 'Link copied!');
      return;
    } catch (err) {
      // Clipboard failed
    }

    // Final fallback - show a toast with selectable link
    toast.info(
      <div>
        <p className="font-medium mb-1">Share this link:</p>
        <p className="text-xs break-all select-all bg-gray-100 p-2 rounded">{shareUrl}</p>
      </div>,
      { duration: 15000 }
    );
  };

  if (loading) {
    return (
      <LoadingSpinner message="Loading hive..." fullScreen />
    );
  }

  if (!hive) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 flex items-center justify-center">
        <p className="text-gray-500">Hive not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 pb-20">
      {/* Header */}
      <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-amber-100 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="p-2 -ml-2 hover:bg-amber-50 rounded-full transition-colors"
              data-testid="hive-detail-back-btn"
            >
              <BackIcon />
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-2xl">{hive.icon || '🐝'}</span>
              <div className="min-w-0">
                <h1 className="font-semibold text-gray-900 truncate" data-testid="hive-detail-name">
                  {hive.name}
                </h1>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <button 
                    onClick={() => setShowMembersModal(true)}
                    className="flex items-center gap-1 hover:text-amber-600 transition-colors"
                  >
                    <UsersIcon />
                    {hive.member_count} members
                  </button>
                  {hive.hive_type === 'private' && (
                    <span className="text-amber-600">Private</span>
                  )}
                </div>
              </div>
            </div>
            {/* Share button */}
            <button
              onClick={handleShareHive}
              className="p-2 hover:bg-amber-50 rounded-full transition-colors"
              data-testid="hive-share-btn"
              title={hive.hive_type === 'private' ? 'Share invite link' : 'Share hive'}
            >
              <ExternalLink size={20} />
            </button>
          </div>
          {hive.description && (
            <p className="text-sm text-gray-600 mt-2 line-clamp-2">{hive.description}</p>
          )}
        </div>
      </div>

      {/* Posts */}
      <div className="max-w-2xl mx-auto px-4 py-3 space-y-2">
        {posts.length === 0 ? (
          <EmptyState
            icon="🍯"
            title="No posts in this hive yet"
            description="Be the first to share a recipe!"
            className="py-12"
          />
        ) : (
          posts.map((post) => (
            <BuzzCard
              key={post.buzz_id}
              post={post}
              variant="hive"
              inlineComments={true}
              onRecipeClick={(p) => {
                if (p.source_recipe_id) {
                  // If user has this recipe, navigate to their copy
                  const userRecipeId = userRecipeMap.get(p.source_recipe_id);
                  if (userRecipeId) {
                    navigate(`/recipe/${userRecipeId}?fromHive=${hive.hive_id}`, {
                      state: { fromHive: hive.hive_id, hiveName: hive.name, hiveIcon: hive.icon }
                    });
                  } else {
                    // Otherwise show the shared/read-only view
                    navigate(`/shared/${p.source_recipe_id}`, { 
                      state: { fromHive: hive.hive_id, hiveName: hive.name, hiveIcon: hive.icon } 
                    });
                  }
                }
              }}
              onLike={() => handleLike(post)}
              onAddComment={async (buzzId, text) => {
                try {
                  const response = await feed.addComment(buzzId, text);
                  // Update local state immediately with the new comment
                  if (response.data?.comment) {
                    setPosts(prevPosts => prevPosts.map(p => {
                      if (p.buzz_id === buzzId) {
                        return {
                          ...p,
                          comments: [...(p.comments || []), response.data.comment],
                          comment_count: (p.comment_count || 0) + 1
                        };
                      }
                      return p;
                    }));
                  }
                  toast.success('Comment added!');
                } catch (error) {
                  console.error('Error adding comment:', error);
                  toast.error('Failed to add comment');
                }
              }}
              onSave={handleSaveRecipe}
              isLiked={likedPosts.has(post.buzz_id) || post.likes?.includes(currentUserId)}
              isSaved={userRecipeIds.has(post.source_recipe_id)}
            />
          ))
        )}
      </div>

      {/* Members Modal */}
      {showMembersModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowMembersModal(false)}
        >
          <div 
            className="bg-white rounded-xl max-w-sm w-full max-h-[70vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-900">Hive Members ({members.length})</h3>
              <button 
                onClick={() => setShowMembersModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(70vh-60px)]">
              {members.length > 0 ? (
                <div className="space-y-3">
                  {members.map((member, idx) => (
                    <div key={member.user_id || idx} className="flex items-center gap-3">
                      {member.user_picture ? (
                        <img 
                          src={member.user_picture} 
                          alt={member.user_name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-lg">
                          {(member.user_name || 'U')[0].toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{member.user_name}</p>
                        <p className="text-xs text-gray-500">
                          {member.role === 'creator' ? '👑 Creator' : 
                           hive?.queens?.includes(member.user_id) ? '👑 Queen' : 'Member'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No members yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default HiveDetailPage;
